<script lang="ts">
  import { onMount } from 'svelte';
  import type {
    AlarmPushStageStatus,
    AlarmThresholdEntry,
    DeviceActivityItem,
    DeviceGroupSummary,
    DeviceIngressKeyResponse,
    DeviceStatus,
    DeviceSummary,
    TenantSummary,
    UpdateDeviceAlarmRequest,
  } from '@ucc/shared';
  import {
    ApiError,
    deleteDevice,
    fetchAlarmPushStatus,
    fetchDevice,
    fetchDeviceActivity,
    fetchDeviceAlarmConfig,
    fetchDeviceIngressKey,
    fetchGroups,
    fetchOtaPingStatus,
    fetchTenants,
    pingOtaDevice,
    releaseDevice,
    updateDeviceAlarmConfig,
  } from '$lib/api';
  import { session } from '$lib/session.svelte';
  import Badge from '$lib/components/Badge.svelte';
  import Card from '$lib/components/Card.svelte';

  let {
    deviceId,
  }: {
    deviceId: string;
  } = $props();

  let device = $state<DeviceSummary | null>(null);
  let tenants = $state<TenantSummary[]>([]);
  let groups = $state<DeviceGroupSummary[]>([]);
  let loading = $state(true);
  let error = $state<string | null>(null);

  let activityItems = $state<DeviceActivityItem[]>([]);
  let activityLoading = $state(false);
  let activityError = $state<string | null>(null);
  let activityFilter = $state<'all' | 'audit' | 'message' | 'upstream'>('all');
  let activityRefreshing = $state(false);

  let releaseOpen = $state(false);
  let releasing = $state(false);

  let deleteOpen = $state(false);
  let deleting = $state(false);
  let deleteError = $state<string | null>(null);
  let deleteUidInput = $state('');

  // Client-only alarm-threshold editing (2026-09-08).
  type AlarmEditValue = { enabled: boolean; debounceHSeconds: string; debounceLSeconds: string; hh: string; h: string; l: string; ll: string };
  let alarmEntries = $state<AlarmThresholdEntry[]>([]);
  let alarmHasOverride = $state(false);
  let alarmUpdatedAt = $state<string | null>(null);
  let alarmLoading = $state(false);
  let alarmBusy = $state(false);
  let alarmError = $state<string | null>(null);
  let alarmEditValues = $state<Record<number, AlarmEditValue>>({});
  /** Mirrors OTAPage.svelte's configKnownUpdatedAt pattern — only reseed edit values when a genuinely new record lands, so a silent poll never clobbers an in-progress edit. */
  let alarmKnownUpdatedAt: string | null = null;
  let alarmPingState = $state<'unknown' | 'checking' | 'online' | 'offline'>('unknown');
  let alarmCheckingOnline = $state(false);
  /** Progress of the most recent Update push (2026-09-09) — so "did it actually work?" has a visible answer instead of just watching the table refresh. */
  let alarmPushStages = $state<AlarmPushStageStatus[]>([]);
  /**
   * Display is armed only by clicking Update, and force-cleared by Check Online — otherwise a
   * confirmed/rejected result from a past Update kept showing forever, including while doing
   * something unrelated like Check Online (Pathma, 2026-09-09). Each new stage change gets its
   * own fresh 5s window rather than a flat 5s from the click, so a real device (which can take a
   * while to reply, especially the manually-operated simulator) doesn't have its final result
   * hidden before the user ever sees it.
   */
  let alarmPushVisible = $state(false);
  let alarmPushArmed = false;
  let alarmPushLastKey: string | null = null;
  let alarmPushHideTimer: ReturnType<typeof setTimeout> | null = null;

  const role = $derived(session.user?.role);
  const isStaff = $derived(role === 'admin' || role === 'user');
  const isClient = $derived(role === 'client');
  let releaseError = $state<string | null>(null);
  let releasedNewPuk = $state<string | null>(null);
  let releasedUid = $state<string | null>(null);
  let pukCopied = $state(false);

  let ingressKeyInfo = $state<DeviceIngressKeyResponse | null>(null);
  let ingressKeyLoading = $state(false);
  let ingressKeyError = $state<string | null>(null);
  let ingressUrlCopied = $state(false);
  let ingressKeyCopied = $state(false);

  const isAdmin = $derived(role === 'admin');
  const actorTenantId = $derived(session.user?.tenantId ?? null);

  const canRelease = $derived.by(() => {
    if (!device || device.ownershipStatus !== 'claimed') return false;
    // Staff (admin|user) may force-release any claimed device.
    if (role === 'admin' || role === 'user') return true;
    if (role === 'client') return device.tenantId === actorTenantId;
    return false;
  });

  // Staff/admin only, and only once not claimed by any client — release first if claimed.
  const canDelete = $derived(Boolean(device && device.ownershipStatus !== 'claimed' && isStaff));

  const tenantLabel = $derived.by(() => {
    if (!device) return '—';
    if (!device.tenantId) return '—';
    return tenants.find((t) => t.id === device!.tenantId)?.name ?? device.tenantId;
  });

  const groupLabel = $derived.by(() => {
    if (!device?.groupId) return 'Unassigned';
    return groups.find((g) => g.id === device!.groupId)?.name ?? device.groupId;
  });

  const devicePoolLabel = $derived(device?.devicePool?.name ?? null);

  function statusTone(status: DeviceStatus): 'success' | 'neutral' | 'warning' | 'danger' | 'accent' {
    switch (status) {
      case 'online':
        return 'success';
      case 'offline':
        return 'neutral';
      case 'disabled':
        return 'danger';
      case 'logging':
        return 'accent';
      default:
        return 'neutral';
    }
  }

  /** UI label — never surface API status "unknown" as text. */
  function statusLabel(status: DeviceStatus): string {
    if (status === 'unknown') return '—';
    return status.charAt(0).toUpperCase() + status.slice(1);
  }

  function formatTs(iso: string | null | undefined): string {
    if (!iso) return '—';
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
  }


  const filteredActivity = $derived(
    activityFilter === 'all'
      ? activityItems
      : activityItems.filter((i) => i.kind === activityFilter),
  );

  function kindLabel(kind: string): string {
    if (kind === 'message') return 'Message';
    if (kind === 'upstream') return 'Upstream';
    return 'Audit';
  }

  function kindTone(kind: string): 'neutral' | 'accent' | 'success' | 'warning' | 'danger' {
    if (kind === 'message') return 'accent';
    if (kind === 'upstream') return 'warning';
    return 'neutral';
  }

  const POLL_MS = 20_000;

  async function load(id: string, opts?: { silent?: boolean }) {
    const silent = opts?.silent === true;
    if (!silent) {
      loading = true;
      error = null;
      device = null;
      activityLoading = true;
      activityError = null;
    }
    try {
      const dRes = await fetchDevice(id);
      device = dRes.device;
      // Secondary lookups must not block detail body
      const [tRes, gRes, aRes] = await Promise.all([
        fetchTenants().catch(() => ({ tenants: [] as TenantSummary[] })),
        fetchGroups().catch(() => ({ groups: [] as DeviceGroupSummary[] })),
        fetchDeviceActivity(id)
          .then((r) => r)
          .catch((err) => {
            if (!silent) {
              activityError = err instanceof ApiError ? err.message : 'Failed to load activity';
            }
            return {
              items: silent ? activityItems : ([] as DeviceActivityItem[]),
              auditCount: 0,
              messageCount: 0,
              upstreamCount: 0,
            };
          }),
      ]);
      tenants = tRes.tenants;
      groups = gRes.groups;
      activityItems = aRes.items;
      activityLoading = false;
      if (silent) error = null;

      if (!silent && isStaff && device.devicePool) {
        void loadIngressKey();
      }
    } catch (err) {
      if (!silent) {
        if (err instanceof ApiError && err.status === 404) {
          error = 'Device not found or not visible in your tenant.';
        } else {
          error = err instanceof ApiError ? err.message : 'Failed to load device';
        }
      }
    } finally {
      if (!silent) {
        loading = false;
      }
    }
  }

  async function refreshActivity() {
    activityRefreshing = true;
    try {
      await load(deviceId, { silent: true });
    } finally {
      activityRefreshing = false;
    }
  }

  function openRelease() {
    releaseOpen = true;
    releaseError = null;
  }

  function cancelRelease() {
    releaseOpen = false;
    releaseError = null;
  }

  async function confirmRelease() {
    if (!device || releasing) return;
    releasing = true;
    releaseError = null;
    try {
      const res = await releaseDevice(device.id, { confirm: true });
      releasedNewPuk = res.newPuk;
      releasedUid = res.device.uid;
      device = null;
      releaseOpen = false;
    } catch (err) {
      releaseError = err instanceof ApiError ? err.message : 'Failed to release device';
    } finally {
      releasing = false;
    }
  }

  function openDelete() {
    deleteOpen = true;
    deleteError = null;
    deleteUidInput = '';
  }

  function cancelDelete() {
    deleteOpen = false;
    deleteError = null;
    deleteUidInput = '';
  }

  async function confirmDelete() {
    if (!device || deleting) return;
    deleting = true;
    deleteError = null;
    try {
      await deleteDevice(device.id, { confirm: true });
      window.location.hash = '#/devices';
    } catch (err) {
      deleteError = err instanceof ApiError ? err.message : 'Failed to delete device';
    } finally {
      deleting = false;
    }
  }

  function profileLabel(p: { meterModel: string; profileId: number; profileVersion: number; isDefault: boolean }): string {
    return `${p.meterModel} · pid ${p.profileId} v${p.profileVersion}${p.isDefault ? ' (default)' : ''}`;
  }

  async function copyNewPuk() {
    if (!releasedNewPuk) return;
    try {
      await navigator.clipboard.writeText(releasedNewPuk);
      pukCopied = true;
      setTimeout(() => {
        pukCopied = false;
      }, 2000);
    } catch {
      /* ignore */
    }
  }

  async function loadIngressKey() {
    if (!device) return;
    ingressKeyLoading = true;
    ingressKeyError = null;
    try {
      ingressKeyInfo = await fetchDeviceIngressKey(device.id);
    } catch (err) {
      ingressKeyError = err instanceof ApiError ? err.message : 'Failed to load ingress credentials';
    } finally {
      ingressKeyLoading = false;
    }
  }

  function ingressUrl(slug: string, uid: string): string {
    return `${window.location.origin}/ingress/https/${slug}/${uid}`;
  }

  async function copyIngressUrl(text: string) {
    try {
      await navigator.clipboard.writeText(text);
      ingressUrlCopied = true;
      setTimeout(() => {
        ingressUrlCopied = false;
      }, 2000);
    } catch {
      /* ignore */
    }
  }

  async function copyIngressKey(text: string) {
    try {
      await navigator.clipboard.writeText(text);
      ingressKeyCopied = true;
      setTimeout(() => {
        ingressKeyCopied = false;
      }, 2000);
    } catch {
      /* ignore */
    }
  }

  function seedAlarmEditValues() {
    const next: Record<number, AlarmEditValue> = {};
    for (const e of alarmEntries) {
      next[e.parameterId] = {
        enabled: e.enabled,
        debounceHSeconds: String(e.debounceHSeconds),
        debounceLSeconds: String(e.debounceLSeconds),
        hh: e.hh == null ? '' : String(e.hh),
        h: e.h == null ? '' : String(e.h),
        l: e.l == null ? '' : String(e.l),
        ll: e.ll == null ? '' : String(e.ll),
      };
    }
    alarmEditValues = next;
  }

  /** `silent` mirrors OTAPage.svelte's loadConfig() — never touches alarmLoading during a poll tick, and only reseeds edit values when updatedAt genuinely changes, so an in-progress edit is never clobbered. */
  async function loadAlarmConfig(silent = false) {
    if (!device) return;
    if (!silent) alarmLoading = true;
    try {
      const res = await fetchDeviceAlarmConfig(device.id);
      const isNew = res.updatedAt !== alarmKnownUpdatedAt;
      alarmEntries = res.entries;
      alarmHasOverride = res.hasOverride;
      alarmUpdatedAt = res.updatedAt;
      if (isNew) {
        seedAlarmEditValues();
        alarmKnownUpdatedAt = res.updatedAt;
      }
      alarmError = null;
    } catch (err) {
      alarmError = err instanceof ApiError ? err.message : 'Failed to load alarm thresholds';
    } finally {
      if (!silent) alarmLoading = false;
    }
  }

  async function loadAlarmPingState() {
    if (!device) return;
    try {
      const res = await fetchOtaPingStatus(device.id);
      alarmPingState = res.pingState;
    } catch {
      /* leave last-known state on a transient failure */
    }
  }

  function stagesKey(stages: AlarmPushStageStatus[]): string {
    return stages.map((s) => `${s.stage}:${s.status}:${s.replyLabel ?? ''}`).join('|');
  }

  /** Called on Update click — the only thing allowed to make the status line appear. */
  function armAlarmPushDisplay() {
    alarmPushArmed = true;
    alarmPushLastKey = null; // forces the next poll tick to (re)start the 5s window
  }

  /** Called on Check Online (an unrelated action) — the status line must not linger into it. */
  function hideAlarmPushDisplay() {
    alarmPushArmed = false;
    alarmPushVisible = false;
    if (alarmPushHideTimer) {
      clearTimeout(alarmPushHideTimer);
      alarmPushHideTimer = null;
    }
  }

  async function loadAlarmPushStatus() {
    if (!device) return;
    try {
      const res = await fetchAlarmPushStatus(device.id);
      alarmPushStages = res.stages;
      if (!alarmPushArmed) return;
      const key = stagesKey(res.stages);
      if (key === alarmPushLastKey) return;
      alarmPushLastKey = key;
      alarmPushVisible = true;
      if (alarmPushHideTimer) clearTimeout(alarmPushHideTimer);
      alarmPushHideTimer = setTimeout(() => {
        alarmPushVisible = false;
      }, 5000);
    } catch {
      /* leave last-known status on a transient failure */
    }
  }

  /** One-line summary of alarmPushStages for display — worst/most-informative stage wins. */
  function alarmPushSummary(): { text: string; tone: 'success' | 'danger' | 'warning' | 'neutral' } | null {
    if (alarmPushStages.length === 0) return null;
    const apply = alarmPushStages.find((s) => s.stage === 'apply');
    const trigger = alarmPushStages.find((s) => s.stage === 'trigger');
    if (apply?.status === 'replied') {
      return apply.replyLabel === 'Applied'
        ? { text: 'Device confirmed: alarm thresholds applied.', tone: 'success' }
        : { text: `Device rejected the update (${apply.replyLabel ?? 'unknown reason'}).`, tone: 'danger' };
    }
    if (apply?.status === 'superseded' || trigger?.status === 'superseded') {
      return { text: 'Superseded by a newer update.', tone: 'neutral' };
    }
    if (trigger?.status === 'replied') {
      return { text: 'Device ready — sending alarm profile…', tone: 'warning' };
    }
    return { text: 'Command sent — waiting for device…', tone: 'warning' };
  }

  async function checkAlarmOnline() {
    if (!device) return;
    hideAlarmPushDisplay();
    alarmCheckingOnline = true;
    try {
      await pingOtaDevice(device.id);
      await loadAlarmPingState();
    } catch (err) {
      alarmError = err instanceof ApiError ? err.message : 'Ping failed';
    } finally {
      alarmCheckingOnline = false;
    }
  }

  async function submitAlarmUpdate() {
    if (!device) return;
    alarmError = null;
    alarmBusy = true;
    armAlarmPushDisplay();
    try {
      const values: UpdateDeviceAlarmRequest['values'] = {};
      for (const [pid, v] of Object.entries(alarmEditValues)) {
        values[pid] = {
          enabled: v.enabled,
          debounceHSeconds: Number(v.debounceHSeconds),
          debounceLSeconds: Number(v.debounceLSeconds),
          hh: v.hh.trim() === '' ? null : Number(v.hh),
          h: v.h.trim() === '' ? null : Number(v.h),
          l: v.l.trim() === '' ? null : Number(v.l),
          ll: v.ll.trim() === '' ? null : Number(v.ll),
        };
      }
      await updateDeviceAlarmConfig(device.id, { values });
      await loadAlarmPushStatus();
    } catch (err) {
      alarmError = err instanceof ApiError ? err.message : 'Failed to send';
    } finally {
      alarmBusy = false;
    }
  }

  onMount(() => {
    void load(deviceId);
    if (isClient) {
      void loadAlarmConfig();
      void loadAlarmPingState();
      void loadAlarmPushStatus();
    }
    const tick = () => {
      if (document.visibilityState === 'visible') void load(deviceId, { silent: true });
    };
    const id = setInterval(tick, POLL_MS);
    const onVis = () => {
      if (document.visibilityState === 'visible') void load(deviceId, { silent: true });
    };
    document.addEventListener('visibilitychange', onVis);

    let alarmPollId: ReturnType<typeof setInterval> | null = null;
    if (isClient) {
      // Faster cadence than the page's own 20s poll, matching OTAPage.svelte's OTA-specific
      // 2.5s poll — staff/client clicking Check Online or Update expects to see the result
      // land within a few seconds, not up to 20.
      alarmPollId = setInterval(() => {
        if (document.visibilityState === 'visible') {
          void loadAlarmConfig(true);
          void loadAlarmPingState();
          void loadAlarmPushStatus();
        }
      }, 2500);
    }

    return () => {
      clearInterval(id);
      if (alarmPollId) clearInterval(alarmPollId);
      if (alarmPushHideTimer) clearTimeout(alarmPushHideTimer);
      document.removeEventListener('visibilitychange', onVis);
    };
  });
</script>

<div class="page">
  <header class="head">
    <div>
      <a class="back" href="#/devices">← Devices</a>
      {#if releasedNewPuk}
        <h2>Device released</h2>
        <p class="sub">Ownership severed · save the new PUK now</p>
      {:else if device}
        <h2 class={isStaff ? 'mono' : ''}>{isStaff ? device.uid : device.name}</h2>
        <p class="sub mono">
          {#if isStaff}SKU {device.sku ?? '—'} · {device.ownershipStatus}{:else}UID {device.uid} · {device.ownershipStatus}{/if}
        </p>
      {:else if loading}
        <h2>Loading device…</h2>
      {:else}
        <h2>Device</h2>
      {/if}
    </div>
    {#if device}
      {#if device.status === 'unknown'}
        <span class="muted-dash">—</span>
      {:else}
        <Badge tone={statusTone(device.status)}>{statusLabel(device.status)}</Badge>
      {/if}
    {/if}
  </header>

  {#if releasedNewPuk}
    <Card title="Release complete" subtitle="One-time credentials for the next owner">
      <p class="warn-banner">
        Save this PUK now — it will not be shown again. Historical ownership for this tenant is
        cleared; the device is unassigned until claimed again.
      </p>
      <dl class="facts">
        <div>
          <dt>UID</dt>
          <dd class="mono">{releasedUid ?? '—'}</dd>
        </div>
        <div>
          <dt>New PUK</dt>
          <dd class="mono puk-value">{releasedNewPuk}</dd>
        </div>
      </dl>
      <div class="row-actions">
        <button type="button" class="btn primary" onclick={() => void copyNewPuk()}>
          {pukCopied ? 'Copied' : 'Copy PUK'}
        </button>
        <a class="btn ghost" href="#/devices">Back to devices</a>
      </div>
    </Card>
  {:else if error}
    <Card title="Unavailable">
      <p class="error">{error}</p>
      <a class="btn ghost" href="#/devices">Back to devices</a>
    </Card>
  {:else if !device}
    <Card title="Device">
      <p class="muted">Loading…</p>
    </Card>
  {:else}
      <Card title="Overview" subtitle="Identity and connectivity">
        <dl class="facts">
          {#if isStaff}
            <div>
              <dt>Client display name</dt>
              <dd>{device.ownershipStatus === 'claimed' ? device.name : '—'}</dd>
            </div>
          {:else}
            <div>
              <dt>Name</dt>
              <dd>{device.name}</dd>
            </div>
            <div>
              <dt>SKU</dt>
              <dd class="mono">{device.sku ?? '—'}</dd>
            </div>
          {/if}
          <div>
            <dt>Ownership</dt>
            <dd class="mono">{device.ownershipStatus}</dd>
          </div>
          {#if isStaff}
            {#if device.ownershipStatus === 'inventory' || device.ownershipStatus === 'released'}
              <div>
                <dt>PUK</dt>
                <dd class="mono puk-value">{device.puk ?? '— (not available)'}</dd>
              </div>
            {:else if device.ownershipStatus === 'claimed'}
              <div>
                <dt>Claimed by client</dt>
                <dd>{device.tenantName ?? device.tenantId ?? '—'}</dd>
              </div>
              {#if device.claimedByEmail}
                <div>
                  <dt>Claimed by user</dt>
                  <dd>{device.claimedByEmail}</dd>
                </div>
              {/if}
              <div>
                <dt>PUK</dt>
                <dd class="muted">Consumed (no longer valid)</dd>
              </div>
            {/if}
          {/if}
          <div>
            <dt>Profile</dt>
            <dd>
              {#if device.profile}
                <a href="#/settings/profiles">{profileLabel(device.profile)}</a>
              {:else}
                <span class="muted-dash">—</span>
              {/if}
            </dd>
          </div>
          <div>
            <dt>Status</dt>
            <dd>
              {#if device.status === 'unknown'}
                <span class="muted-dash">—</span>
              {:else}
                <Badge tone={statusTone(device.status)}>{statusLabel(device.status)}</Badge>
              {/if}
            </dd>
          </div>
          <div>
            <dt>Last seen</dt>
            <dd>{formatTs(device.lastSeenAt)}</dd>
          </div>
          <div>
            <dt>Tenant</dt>
            <dd>{tenantLabel}</dd>
          </div>
          {#if isStaff}
            <div>
              <dt>Device Pool</dt>
              <dd>
                {#if device.devicePool}
                  <a href="#/device-pools/{device.devicePool.id}">{devicePoolLabel}</a>
                {:else}
                  <span class="muted-dash">Not assigned</span>
                {/if}
              </dd>
            </div>
            {#if device.devicePool}
              <div>
                <dt>Ingress</dt>
                <dd>
                  {#if ingressKeyError}
                    <span class="error">{ingressKeyError}</span>
                  {:else if !ingressKeyInfo}
                    <span class="muted">Loading…</span>
                  {:else}
                    {@const ep = ingressKeyInfo.endpoint}
                    {#if ep?.type === 'https' && ep.slug}
                      <div class="mono wrap">{ingressUrl(ep.slug, ingressKeyInfo.uid)}</div>
                      <div class="mono wrap">x-api-key: {ingressKeyInfo.key}</div>
                    {:else}
                      <span class="muted">Via {ep?.type ?? 'not set'} — no credentials to show, the broker handles auth</span>
                    {/if}
                  {/if}
                </dd>
              </div>
            {/if}
          {:else}
            <div>
              <dt>Group</dt>
              <dd>{groupLabel}</dd>
            </div>
          {/if}
          {#if isAdmin}
            <div>
              <dt>Claimed at</dt>
              <dd>{formatTs(device.claimedAt)}</dd>
            </div>
            <div>
              <dt>Released at</dt>
              <dd>{formatTs(device.releasedAt)}</dd>
            </div>
          {/if}
        </dl>

        {#if canRelease}
          <div class="release-zone">
            {#if !releaseOpen}
              <p class="muted action-lead">
                Release permanently severs this device from the current tenant, clears group
                membership, and issues a new PUK for the next owner.
                <button type="button" class="link-btn" onclick={openRelease}>Release device</button>
              </p>
            {:else}
              <div class="release-panel">
                <p class="warn-banner">
                  This cannot be undone. Device history for this ownership is purged/marked; a new
                  PUK is issued once.
                </p>
                {#if releaseError}
                  <p class="error">{releaseError}</p>
                {/if}
                <div class="row-actions">
                  <button
                    type="button"
                    class="btn danger"
                    disabled={releasing}
                    onclick={() => void confirmRelease()}
                  >
                    {releasing ? 'Releasing…' : 'Confirm release'}
                  </button>
                  <button type="button" class="btn ghost" disabled={releasing} onclick={cancelRelease}>
                    Cancel
                  </button>
                </div>
              </div>
            {/if}
          </div>
        {/if}

        {#if canDelete}
          <div class="release-zone">
            {#if !deleteOpen}
              <p class="muted action-lead">
                Delete permanently removes this device from inventory and destroys its entire
                message, reading, and OTA history — this cannot be undone.
                <button type="button" class="link-btn" onclick={openDelete}>Delete device</button>
              </p>
            {:else}
              <div class="release-panel">
                <p class="warn-banner">
                  This cannot be undone. Type the UID <strong class="mono">{device.uid}</strong> to confirm.
                </p>
                <input
                  class="confirm-input"
                  type="text"
                  placeholder={device.uid}
                  bind:value={deleteUidInput}
                  disabled={deleting}
                />
                {#if deleteError}
                  <p class="error">{deleteError}</p>
                {/if}
                <div class="row-actions">
                  <button
                    type="button"
                    class="btn danger"
                    disabled={deleting || deleteUidInput.trim().toUpperCase() !== device.uid.toUpperCase()}
                    onclick={() => void confirmDelete()}
                  >
                    {deleting ? 'Deleting…' : 'Confirm delete'}
                  </button>
                  <button type="button" class="btn ghost" disabled={deleting} onclick={cancelDelete}>
                    Cancel
                  </button>
                </div>
              </div>
            {/if}
          </div>
        {/if}
      </Card>

      {#if isClient}
        <Card title="Alarm Thresholds" subtitle="Edit this device's alarm settings and send them over the air">
          {#if alarmLoading && alarmEntries.length === 0}
            <p class="muted">Loading…</p>
          {:else if alarmEntries.length === 0}
            <p class="muted banner">
              {alarmError ?? 'No alarm-capable parameters on this device.'}
            </p>
          {:else}
            <p class="muted small">
              {alarmHasOverride ? 'Last confirmed applied' : 'Showing profile defaults — never customized'}:
              {alarmUpdatedAt ? new Date(alarmUpdatedAt).toLocaleString() : '—'}
            </p>
            <div class="table-wrap">
              <table class="alarm-table">
                <thead>
                  <tr>
                    <th>Parameter</th>
                    <th>Enabled</th>
                    <th>Debounce H (s)</th>
                    <th>Debounce L (s)</th>
                    <th>HH</th>
                    <th>H</th>
                    <th>L</th>
                    <th>LL</th>
                  </tr>
                </thead>
                <tbody>
                  {#each alarmEntries as entry (entry.parameterId)}
                    {@const v = alarmEditValues[entry.parameterId]}
                    {#if v}
                      <tr>
                        <td>{entry.name}{entry.unit ? ` (${entry.unit})` : ''}</td>
                        <td>
                          <input type="checkbox" bind:checked={v.enabled} disabled={alarmBusy} />
                        </td>
                        <td><input class="num-input" type="text" bind:value={v.debounceHSeconds} disabled={alarmBusy} /></td>
                        <td><input class="num-input" type="text" bind:value={v.debounceLSeconds} disabled={alarmBusy} /></td>
                        <td><input class="num-input" type="text" bind:value={v.hh} disabled={alarmBusy || !v.enabled} /></td>
                        <td><input class="num-input" type="text" bind:value={v.h} disabled={alarmBusy || !v.enabled} /></td>
                        <td><input class="num-input" type="text" bind:value={v.l} disabled={alarmBusy || !v.enabled} /></td>
                        <td><input class="num-input" type="text" bind:value={v.ll} disabled={alarmBusy || !v.enabled} /></td>
                      </tr>
                    {/if}
                  {/each}
                </tbody>
              </table>
            </div>
          {/if}

          {#if alarmEntries.length > 0}
            <div class="online-row">
              <span class="state-badge" data-tone={alarmPingState === 'online' ? 'success' : alarmPingState === 'offline' ? 'danger' : alarmPingState === 'checking' ? 'warning' : 'neutral'}>
                {alarmPingState === 'unknown' ? 'Not checked' : alarmPingState}
              </span>
              <button type="button" class="btn ghost" disabled={alarmCheckingOnline} onclick={() => void checkAlarmOnline()}>
                {alarmCheckingOnline ? 'Checking…' : 'Check Online'}
              </button>
            </div>

            {#if alarmError}
              <p class="error">{alarmError}</p>
            {/if}

            <div class="row-actions">
              <button
                type="button"
                class="btn primary"
                disabled={alarmBusy || alarmPingState !== 'online'}
                onclick={() => void submitAlarmUpdate()}
              >
                {alarmBusy ? 'Sending…' : 'Update Device'}
              </button>
            </div>
            {#if alarmPushVisible && alarmPushSummary()}
              {@const summary = alarmPushSummary()!}
              <p class="push-status" data-tone={summary.tone}>{summary.text}</p>
            {/if}
            {#if alarmPingState !== 'online'}
              <p class="muted small">Check Online first — sending is disabled until the device confirms it's reachable.</p>
            {/if}
          {/if}
        </Card>
      {/if}

      <Card title="Activity log" subtitle="Audit trail for this device · last 100 messages">
        <div class="activity-toolbar">
          <div class="toolbar-row">
            <div class="seg" role="tablist" aria-label="Activity filter">
              <button
                type="button"
                class:active={activityFilter === 'all'}
                onclick={() => (activityFilter = 'all')}
              >All</button>
              <button
                type="button"
                class:active={activityFilter === 'audit'}
                onclick={() => (activityFilter = 'audit')}
              >Audit</button>
              <button
                type="button"
                class:active={activityFilter === 'message'}
                onclick={() => (activityFilter = 'message')}
              >Messages</button>
              {#if isClient}
                <button
                  type="button"
                  class:active={activityFilter === 'upstream'}
                  onclick={() => (activityFilter = 'upstream')}
                >Upstream</button>
              {/if}
            </div>
            <button type="button" class="btn ghost" disabled={activityRefreshing} onclick={() => void refreshActivity()}>
              {activityRefreshing ? 'Refreshing…' : 'Refresh'}
            </button>
          </div>
          <p class="muted tiny">
            UCC keeps the last 1000 device message events even if client retention is 0-day.
          </p>
        </div>

        {#if activityLoading}
          <p class="muted">Loading activity…</p>
        {:else if activityError}
          <p class="error">{activityError}</p>
        {:else if filteredActivity.length === 0}
          <p class="muted empty-activity">
            {#if activityFilter === 'message'}
              No Message from device yet
            {:else if activityFilter === 'audit'}
              No audit events yet
            {:else if activityFilter === 'upstream'}
              No upstream deliveries yet
            {:else}
              No activity yet
            {/if}
          </p>
        {:else}
          <ul class="log">
            {#each filteredActivity as item (item.id)}
              <li class="log-row">
                <div class="log-meta">
                  <Badge tone={kindTone(item.kind)}>{kindLabel(item.kind)}</Badge>
                  <time datetime={item.at}>{formatTs(item.at)}</time>
                </div>
                <div class="log-body">
                  <strong class="log-title mono">{item.title}</strong>
                  {#if item.actorEmail}
                    <span class="muted tiny">{item.actorEmail}</span>
                  {/if}
                  {#if item.direction}
                    <span class="muted tiny"
                      >{item.direction}{#if item.source} · {item.source.toUpperCase()}{/if}{#if item.topic} ·
                      {item.topic}{/if}</span
                    >
                  {/if}
                  {#if item.detail}
                    <pre class="log-detail">{item.detail}</pre>
                  {/if}
                </div>
              </li>
            {/each}
          </ul>
        {/if}
      </Card>
  {/if}
</div>

<style>
  .muted-dash {
    color: var(--muted, #6b7280);
  }

  .page {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1rem;
  }

  .back {
    display: inline-block;
    margin-bottom: 0.4rem;
    font-size: var(--text-sm, 0.9rem);
    color: var(--text-muted);
    text-decoration: none;
  }

  .back:hover {
    color: var(--text);
  }

  h2 {
    margin: 0;
    font-size: 1.35rem;
  }

  .sub {
    margin: 0.25rem 0 0;
    color: var(--text-muted);
    font-size: var(--text-sm, 0.9rem);
  }

  .facts {
    display: grid;
    gap: 0.75rem 1.25rem;
    margin: 0;
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .facts dt {
    margin: 0;
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--text-muted);
  }

  .facts dd {
    margin: 0.15rem 0 0;
    font-size: 0.95rem;
    font-weight: 500;
  }

  .facts dd > div + div {
    margin-top: 0.35rem;
  }

  .mono {
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  }

  .wrap {
    word-break: break-all;
  }

  .muted {
    color: var(--text-muted);
    margin: 0;
  }

  .error {
    color: var(--danger, #b91c1c);
    margin: 0 0 0.75rem;
  }

  .action-lead {
    margin: 0 0 0.85rem;
    font-size: var(--text-sm, 0.9rem);
  }

  .release-zone {
    margin-top: 1rem;
    padding-top: 1rem;
    border-top: 1px solid var(--border);
  }

  .link-btn {
    background: none;
    border: none;
    padding: 0;
    font: inherit;
    color: var(--danger, #b91c1c);
    text-decoration: underline;
    cursor: pointer;
  }

  .release-panel {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  .warn-banner {
    margin: 0 0 0.25rem;
    padding: 0.65rem 0.75rem;
    border-radius: 8px;
    background: color-mix(in srgb, #f59e0b 12%, transparent);
    border: 1px solid color-mix(in srgb, #f59e0b 35%, transparent);
    color: var(--text);
    font-size: var(--text-sm, 0.9rem);
  }

  .puk-value {
    font-size: 1.15rem;
    font-weight: 600;
    letter-spacing: 0.06em;
  }

  .confirm-input {
    font: inherit;
    padding: 0.5rem 0.65rem;
    border-radius: 8px;
    border: 1px solid var(--border);
    background: var(--surface-2, #121218);
    color: var(--text);
  }

  .row-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    margin-top: 0.25rem;
  }

  .btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.35rem;
    border-radius: 8px;
    border: 1px solid transparent;
    padding: 0.5rem 0.85rem;
    font: inherit;
    font-size: var(--text-sm, 0.9rem);
    cursor: pointer;
    text-decoration: none;
  }

  .btn:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }

  .btn.primary {
    background: var(--primary, #2563eb);
    color: #fff;
  }

  .btn.danger {
    background: #b91c1c;
    color: #fff;
  }

  .btn.ghost {
    background: transparent;
    border-color: var(--border);
    color: var(--text);
  }

  @media (max-width: 800px) {
    .facts {
      grid-template-columns: 1fr;
    }
  }

  .activity-toolbar {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    margin-bottom: 0.75rem;
  }
  .toolbar-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
    flex-wrap: wrap;
  }

  .seg {
    display: inline-flex;
    gap: 0.25rem;
    padding: 0.2rem;
    border-radius: 999px;
    background: color-mix(in srgb, var(--surface-2, #111827) 80%, transparent);
    border: 1px solid var(--border, #1f2937);
    width: fit-content;
  }

  .seg button {
    appearance: none;
    border: 0;
    background: transparent;
    color: var(--text-muted, #94a3b8);
    font: inherit;
    font-size: 0.78rem;
    font-weight: 600;
    padding: 0.35rem 0.75rem;
    border-radius: 999px;
    cursor: pointer;
  }

  .seg button.active {
    background: color-mix(in srgb, var(--accent, #38bdf8) 18%, transparent);
    color: var(--text, #e2e8f0);
  }

  .tiny {
    font-size: 0.75rem;
  }

  .empty-activity {
    margin: 0.25rem 0 0;
    line-height: 1.45;
  }

  .log {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 0.65rem;
    max-height: 28rem;
    overflow: auto;
  }

  .log-row {
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
    padding: 0.65rem 0.75rem;
    border-radius: 0.65rem;
    border: 1px solid var(--border, #1f2937);
    background: color-mix(in srgb, var(--surface-2, #0b1220) 70%, transparent);
  }

  .log-meta {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
  }

  .log-meta time {
    font-size: 0.75rem;
    color: var(--text-muted, #94a3b8);
    font-variant-numeric: tabular-nums;
  }

  .log-body {
    display: flex;
    flex-direction: column;
    gap: 0.2rem;
    min-width: 0;
  }

  .log-title {
    font-size: 0.88rem;
    word-break: break-word;
  }

  .log-detail {
    margin: 0.25rem 0 0;
    padding: 0.5rem 0.6rem;
    border-radius: 0.45rem;
    background: color-mix(in srgb, #000 35%, transparent);
    border: 1px solid var(--border, #1f2937);
    color: var(--text-muted, #94a3b8);
    font-size: 0.75rem;
    white-space: pre-wrap;
    word-break: break-word;
    max-height: 8rem;
    overflow: auto;
  }

  .banner {
    padding: 1rem 0;
    margin: 0;
  }

  .table-wrap {
    overflow-x: auto;
  }

  .alarm-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.85rem;
  }

  .alarm-table th,
  .alarm-table td {
    text-align: left;
    padding: 0.4rem 0.4rem;
    border-bottom: 1px solid var(--border, #2a2a32);
    vertical-align: middle;
    white-space: nowrap;
  }

  .alarm-table th {
    color: var(--text-muted);
    font-weight: 500;
    font-size: 0.75rem;
  }

  .num-input {
    font: inherit;
    width: 5.5rem;
    padding: 0.3rem 0.4rem;
    border-radius: 0.4rem;
    border: 1px solid var(--border, #2a2a32);
    background: var(--surface-2, #121218);
    color: var(--text);
    box-sizing: border-box;
  }

  .num-input:disabled {
    opacity: 0.5;
  }

  .online-row {
    display: flex;
    align-items: center;
    gap: 0.65rem;
    margin: 0.75rem 0;
  }

  .state-badge {
    font-size: 0.7rem;
    padding: 0.15rem 0.5rem;
    border-radius: 999px;
    background: var(--surface-2, #eee);
    color: var(--text-muted);
    white-space: nowrap;
    text-transform: capitalize;
  }

  .state-badge[data-tone='success'] {
    background: color-mix(in srgb, var(--success, #22c55e) 18%, transparent);
    color: var(--success, #22c55e);
  }

  .state-badge[data-tone='warning'] {
    background: color-mix(in srgb, #f59e0b 18%, transparent);
    color: #f59e0b;
  }

  .state-badge[data-tone='danger'] {
    background: color-mix(in srgb, #f87171 18%, transparent);
    color: #f87171;
  }

  .push-status {
    margin: 0.4rem 0 0;
    font-size: 0.85rem;
    font-weight: 600;
  }

  .push-status[data-tone='success'] {
    color: var(--success, #22c55e);
  }

  .push-status[data-tone='warning'] {
    color: #f59e0b;
  }

  .push-status[data-tone='danger'] {
    color: #f87171;
  }

  .push-status[data-tone='neutral'] {
    color: var(--text-muted);
  }
</style>
